import { useEffect, useRef } from "react";

/**
 * Hook para parallax suave y visible en la imagen del encabezado
 * - Calcula mientras el encabezado esté visible en pantalla (scrollY <= 650)
 * - Throttling vía requestAnimationFrame
 * - Listener pasivo { passive: true }
 * - translateY(scrollY * 0.35) con límite máx de 90px, conservando scale(1.18)
 * - Desactivado en pantallas <768px y con prefers-reduced-motion
 */
export default function useParallaxHeader() {
  const containerRef = useRef(null);
  const imgRef = useRef(null);

  useEffect(() => {
    // 1. Verificar reducción de movimiento del sistema
    const motionQuery =
      typeof window !== "undefined" && window.matchMedia
        ? window.matchMedia("(prefers-reduced-motion: reduce)")
        : { matches: false };

    if (motionQuery.matches) {
      return;
    }

    // 2. Desactivar en pantallas menores a 768px
    if (typeof window !== "undefined" && window.innerWidth < 768) {
      return;
    }

    const img = imgRef.current;
    if (!img) return;

    let rafId = null;

    const updatePosition = () => {
      rafId = null;
      if (!img) return;

      if (window.innerWidth < 768 || motionQuery.matches) {
        img.style.transform = "scale(1.18)";
        return;
      }

      const scrollY =
        window.pageYOffset || document.documentElement.scrollTop || 0;

      // Si el encabezado ya salió completamente de pantalla, no calcular
      if (scrollY > 650) return;

      // translateY(scrollY * 0.35 px) con límite máximo de 90px
      const translateY = Math.min(Math.max(scrollY * 0.35, 0), 90);
      img.style.transform = `translate3d(0, ${translateY}px, 0) scale(1.18)`;
    };

    const onScroll = () => {
      const scrollY =
        window.pageYOffset || document.documentElement.scrollTop || 0;
      if (scrollY > 650) return;

      if (rafId === null) {
        rafId = requestAnimationFrame(updatePosition);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });

    const handleResize = () => {
      if (window.innerWidth < 768 || motionQuery.matches) {
        if (img) img.style.transform = "scale(1.18)";
      } else {
        onScroll();
      }
    };
    window.addEventListener("resize", handleResize, { passive: true });

    // Cálculo inicial diferido para no bloquear el primer frame de renderizado
    rafId = requestAnimationFrame(updatePosition);

    return () => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", handleResize);
      if (img) {
        img.style.transform = "";
      }
    };
  }, []);

  return { containerRef, imgRef };
}
